/**
 * LazyMedia - 懒加载媒体组件
 * 支持图片、视频等大文件的懒加载
 * 使用 Intersection Observer API 实现性能优化
 */

import React, { useState, useEffect, useRef } from 'react';
import './LazyMedia.css';

/**
 * 懒加载图片组件
 */
export function LazyImage({ src, alt, className, style, placeholder, onLoad, priority = false }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // 高优先级立即加载
  const imgRef = useRef(null);

  useEffect(() => {
    // 高优先级图片跳过懒加载
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // 提前50px开始加载
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  return (
    <div
      ref={imgRef}
      className={`lazy-image-container ${className || ''}`}
      style={style}
    >
      {!isLoaded && (
        <div className="lazy-image-placeholder">
          {placeholder || (
            <div className="lazy-image-skeleton">
              <div className="skeleton-shimmer"></div>
            </div>
          )}
        </div>
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`lazy-image ${isLoaded ? 'lazy-image-loaded' : 'lazy-image-loading'}`}
          onLoad={handleLoad}
          loading="lazy" // 浏览器原生懒加载作为备用
        />
      )}
    </div>
  );
}

/**
 * 懒加载视频组件
 * 优化：使用固定宽高比容器，避免视频加载后布局跳动
 */
export function LazyVideo({
  src,
  poster,
  className,
  style,
  controls = true,
  autoPlay = false,
  loop = false,
  muted = false,
  priority = false,
  onLoad
}) {
  const [isInView, setIsInView] = useState(priority);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px',
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoadedData = () => {
    console.log('[LazyVideo] 视频元数据加载完成, 当前滚动位置:', window.scrollY);
    setIsLoaded(true);
    // 延迟检查滚动位置是否被重置
    setTimeout(() => {
      console.log('[LazyVideo] setIsLoaded后滚动位置:', window.scrollY);
    }, 50);
    if (onLoad) onLoad();
  };

  return (
    <div
      ref={containerRef}
      className={`lazy-video-container ${className || ''}`}
      style={style}
    >
      {/* 占位符：视频加载完成后隐藏但不移除DOM，避免布局跳动 */}
      <div
        className="lazy-video-placeholder"
        style={{ opacity: isLoaded ? 0 : 1, transition: 'opacity 0.3s ease' }}
      >
        {poster ? (
          <img src={poster} alt="Video thumbnail" className="lazy-video-poster" />
        ) : (
          <div className="lazy-video-skeleton">
            <div className="skeleton-shimmer"></div>
            <div className="video-play-icon">▶</div>
          </div>
        )}
      </div>
      {/* 视频：进入视口后开始加载 */}
      {isInView && (
        <video
          src={src}
          poster={poster}
          className={`lazy-video ${isLoaded ? 'lazy-video-loaded' : 'lazy-video-loading'}`}
          controls={controls}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          onLoadedData={handleLoadedData}
          preload="metadata"
          playsInline // 移动端内联播放
        />
      )}
    </div>
  );
}

/**
 * 懒加载 PDF/文件预览按钮
 * 不直接加载文件，点击时才触发加载
 */
export function LazyFileButton({
  url,
  fileName,
  fileType = 'pdf',
  onPreview,
  className,
  style,
  buttonStyle
}) {
  const [isPreloading, setIsPreloading] = useState(false);
  const buttonRef = useRef(null);

  // 可选：鼠标悬停时预加载
  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const handleMouseEnter = () => {
      if (!isPreloading) {
        setIsPreloading(true);
        // 创建预加载
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
      }
    };

    button.addEventListener('mouseenter', handleMouseEnter);
    return () => button.removeEventListener('mouseenter', handleMouseEnter);
  }, [url, isPreloading]);

  const handleClick = (e) => {
    e.preventDefault();
    if (onPreview) {
      onPreview({ url, name: fileName, type: fileType });
    }
  };

  return (
    <button
      ref={buttonRef}
      className={`lazy-file-button ${className || ''}`}
      style={style || buttonStyle}
      onClick={handleClick}
      type="button"
    >
      {fileName}
    </button>
  );
}

/**
 * 通用懒加载媒体组件
 * 根据类型自动选择合适的加载策略
 */
export default function LazyMedia({
  type,
  src,
  alt,
  className,
  style,
  priority = false,
  ...props
}) {
  switch (type) {
    case 'image':
      return (
        <LazyImage
          src={src}
          alt={alt}
          className={className}
          style={style}
          priority={priority}
          {...props}
        />
      );

    case 'video':
      return (
        <LazyVideo
          src={src}
          className={className}
          style={style}
          priority={priority}
          {...props}
        />
      );

    default:
      return null;
  }
}
